import { z } from 'zod';

/** Papéis do painel. A ordem importa: cada papel herda o anterior. */
export const ROLES = ['viewer', 'reviewer', 'admin', 'owner'] as const;
export const roleSchema = z.enum(ROLES);
export type Role = z.infer<typeof roleSchema>;

/** Papéis que o owner pode atribuir pela UI. `owner` só entra via seed/ops. */
export const ASSIGNABLE_ROLES = ['viewer', 'reviewer', 'admin'] as const;
export const assignableRoleSchema = z.enum(ASSIGNABLE_ROLES);
export type AssignableRole = z.infer<typeof assignableRoleSchema>;

const ROLE_RANK: Record<Role, number> = { viewer: 0, reviewer: 1, admin: 2, owner: 3 };

export function roleAtLeast(actual: Role, required: Role): boolean {
  return ROLE_RANK[actual] >= ROLE_RANK[required];
}

/** Módulos operacionais atribuíveis. `admin` e `accounts` são exclusivos do owner. */
export const ASSIGNABLE_MODULES = [
  'dashboard',
  'products',
  'review',
  'runs',
  'write_operations',
  'settings',
] as const;
export const assignableModuleSchema = z.enum(ASSIGNABLE_MODULES);
export type AppModule = z.infer<typeof assignableModuleSchema>;

export const MODULE_CATALOG: Record<
  AppModule,
  { label: string; description: string; path: string }
> = {
  dashboard: {
    label: 'Visão geral',
    description: 'Saúde do catálogo, scores e regras.',
    path: '/dashboard',
  },
  products: {
    label: 'Produtos',
    description: 'Lista e detalhe do catálogo local.',
    path: '/products',
  },
  review: {
    label: 'Fila de revisão',
    description: 'Aprovar ou rejeitar sugestões.',
    path: '/review',
  },
  runs: {
    label: 'Execuções',
    description: 'Ingestão, auditoria, geração e publicação na VTEX.',
    path: '/runs',
  },
  write_operations: {
    label: 'Publicações',
    description: 'Envios à VTEX, checagem e rollback.',
    path: '/write-operations',
  },
  settings: {
    label: 'Configurações',
    description: 'Provedor de IA, conta atual e guia de estilo.',
    path: '/settings',
  },
};

export function userHasModule(
  user: { role: Role; modules: readonly AppModule[] },
  module: AppModule,
): boolean {
  return user.role === 'owner' || user.modules.includes(module);
}

/** Primeira rota que o usuário pode abrir depois do login. */
export function firstAllowedPath(user: {
  role: Role;
  modules: readonly AppModule[];
}): string {
  if (user.role === 'owner') return '/dashboard';
  for (const module of ASSIGNABLE_MODULES) {
    if (user.modules.includes(module)) return MODULE_CATALOG[module].path;
  }
  return '/no-access';
}

/**
 * Campos que o sistema sabe auditar e enriquecer.
 *
 * `linkId` está deliberadamente fora: alterá-lo muda a URL do produto e quebra
 * links já indexados. Só entra em escopo com decisão explícita do cliente.
 */
export const ENRICHABLE_FIELDS = [
  'name',
  'title',
  'description',
  'metaTagDescription',
  'keywords',
  'specifications',
  'imageAltText',
  'imageLabel',
  'skuName',
] as const;
export const enrichableFieldSchema = z.enum(ENRICHABLE_FIELDS);
export type EnrichableField = z.infer<typeof enrichableFieldSchema>;

/** Campos que o writer sabe mesclar no PUT de produto. */
export const PRODUCT_LEVEL_FIELDS = [
  'name',
  'title',
  'description',
  'metaTagDescription',
  'keywords',
] as const satisfies readonly EnrichableField[];

export type ProductLevelField = (typeof PRODUCT_LEVEL_FIELDS)[number];

export function isProductLevelField(field: EnrichableField): field is ProductLevelField {
  return (PRODUCT_LEVEL_FIELDS as readonly string[]).includes(field);
}

/** Campos de nível SKU (exceto imagens, que também usam fileId). */
export const SKU_LEVEL_FIELDS = ['skuName'] as const satisfies readonly EnrichableField[];
export type SkuLevelField = (typeof SKU_LEVEL_FIELDS)[number];

export function isSkuLevelField(field: EnrichableField): field is SkuLevelField {
  return (SKU_LEVEL_FIELDS as readonly string[]).includes(field);
}

export const SEVERITIES = ['info', 'low', 'medium', 'high', 'critical'] as const;
export const severitySchema = z.enum(SEVERITIES);
export type Severity = z.infer<typeof severitySchema>;

/** Peso de cada severidade no cálculo de score. */
export const SEVERITY_WEIGHT: Record<Severity, number> = {
  info: 1,
  low: 3,
  medium: 8,
  high: 18,
  critical: 35,
};

export const SUGGESTION_STATUSES = [
  'pending',
  'rejected_by_validator',
  'approved',
  'rejected',
  'applied',
  'failed',
  'superseded',
] as const;
export const suggestionStatusSchema = z.enum(SUGGESTION_STATUSES);
export type SuggestionStatus = z.infer<typeof suggestionStatusSchema>;

/**
 * Statuses que já saíram da fila de revisão (histórico).
 * Exclui `pending` e `rejected_by_validator`.
 */
export const HISTORY_SUGGESTION_STATUSES = [
  'approved',
  'rejected',
  'applied',
  'failed',
  'superseded',
] as const satisfies readonly SuggestionStatus[];
export type HistorySuggestionStatus = (typeof HISTORY_SUGGESTION_STATUSES)[number];

/** Página de histórico de enriquecimento — mesma permissão do módulo `review`. */
export const HISTORY_PAGE = {
  label: 'Histórico',
  description: 'Decisões de revisão: aguardando publicação, recusadas ou publicadas na VTEX.',
  path: '/history',
} as const;

/**
 * Origem final do valor gravado. Alimenta a rotulagem obrigatória do
 * Merchant Center (`digital_source_type = trained_algorithmic_media`).
 */
export const PROVENANCES = ['ai', 'ai_edited', 'human'] as const;
export const provenanceSchema = z.enum(PROVENANCES);
export type Provenance = z.infer<typeof provenanceSchema>;

export function provenanceIsAiGenerated(provenance: Provenance): boolean {
  return provenance === 'ai' || provenance === 'ai_edited';
}

export const WRITE_STATUSES = [
  'pending',
  'dry_run',
  'succeeded',
  'failed',
  'aborted_conflict',
  'rolled_back',
] as const;
export const writeStatusSchema = z.enum(WRITE_STATUSES);
export type WriteStatus = z.infer<typeof writeStatusSchema>;

export const RUN_KINDS = ['ingestion', 'audit', 'generation', 'write'] as const;
export const runKindSchema = z.enum(RUN_KINDS);
export type RunKind = z.infer<typeof runKindSchema>;

export const RUN_STATUSES = [
  'queued',
  'running',
  'succeeded',
  'failed',
  'cancelled',
  'paused',
] as const;
export const runStatusSchema = z.enum(RUN_STATUSES);
export type RunStatus = z.infer<typeof runStatusSchema>;

export const VTEX_ENVIRONMENTS = ['vtexcommercestable', 'vtexcommercebeta'] as const;
export const vtexEnvironmentSchema = z.enum(VTEX_ENVIRONMENTS);
export type VtexEnvironment = z.infer<typeof vtexEnvironmentSchema>;
